import { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useAuthedVideo — Fetches a JWT-protected video endpoint as a Blob URL.
 *
 * The <video src> attribute cannot send Authorization headers, so this hook
 * fetches the video via axios with the Bearer token, creates an object URL
 * from the blob, and cleans it up on unmount to prevent memory leaks.
 *
 * @param {string|null} sessionId  - interview-sim session ID
 * @param {'instructor'|'student'} role - kept for API symmetry; both roles use
 *   the same endpoint GET /api/cabin/interview-sim/{id}/video (backend allows
 *   the session owner OR an instructor/admin to stream it).
 * @returns {{ videoUrl: string|null, loading: boolean, error: string|null }}
 */
export function useAuthedVideo(sessionId, role = 'instructor') {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return;

    let revoked = false;
    let objectUrl = null;

    async function loadVideo() {
      setLoading(true);
      setError(null);
      try {
        const token = getToken();
        // Both roles use the cabin endpoint; backend authorizes owner OR instructor.
        const endpoint = `${API}/cabin/interview-sim/${sessionId}/video`;

        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });

        if (revoked) return;
        objectUrl = URL.createObjectURL(res.data);
        setVideoUrl(objectUrl);
      } catch (err) {
        if (!revoked) {
          setError(
            err?.response?.data?.message_tr ||
              err?.response?.data?.detail ||
              err.message
          );
        }
      } finally {
        if (!revoked) setLoading(false);
      }
    }

    loadVideo();

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sessionId, role]);

  return { videoUrl, loading, error };
}
